import * as moment from 'moment';
import { IMicrositeRecipientForList } from "@nkr/models/microsites"
import { ViewWrapper } from "./styles"
import { Button, Checkbox, Col, ControlLabel, FormControl, FormGroup, Glyphicon, Label, Panel, Row } from "react-bootstrap"
import { useState } from "react";
import { IListItem } from '@nkr/models/list-item';
import { IChangeCoachStatusRequest } from '@nkr/store/microsites/actions-coach';
import { CoachStatus } from '@nkr/enums/index';

interface IProps {
    data: IMicrositeRecipientForList,
    coachStatusList: IListItem[],
    onCoachStatusChange: (recipient: IMicrositeRecipientForList, request: IChangeCoachStatusRequest) => void,
}

export default function CoachStatusForm({data, coachStatusList, onCoachStatusChange}: IProps) {
    const defaultTxpDate = data.transplantDate ? data.transplantDate : data.mostRecentCoachNotificationEvent?.eventDate //use transplant date if we have it otherwise use the coach notification date

    const [coachStatus, setCoachStatus] = useState<number>(data.coachStatus ? data.coachStatus : -1);
    const [nextFollowUpDate, setNextFollowUpDate] = useState<string>(data.nextFollowupDate ? moment(data.nextFollowupDate).format('YYYY-MM-DD') : '');
    const [transplantDate, setTransplantDate] = useState<string>(defaultTxpDate ? moment(defaultTxpDate).format('YYYY-MM-DD') : '');
    const [mostRecentContactDate, setMostRecentContactDate] = useState<string>(data.mostRecentCoachContactDateTime ? moment(data.mostRecentCoachContactDateTime).format('YYYY-MM-DD') : '');
    const [isCoachingDesired, setIsCoachingDesired] = useState<boolean>(data.isCoachingDesired);

    const statusIsDirty = coachStatus != data.coachStatus;
    const nextFollowUpDateIsPast = moment(nextFollowUpDate).isValid() ? moment(nextFollowUpDate).isBefore(moment(), 'day') : false;
    const nextFollowUpDateIsToday = moment(nextFollowUpDate).isValid() ? moment(nextFollowUpDate).isSame(moment(), 'day') : false;
    const nextFollowUpDateIsDirty = !moment(nextFollowUpDate).isSame(moment(data.nextFollowupDate));
    const nextFollowUpDateIsRequired = nextFollowUpDateIsDirty || (statusIsDirty && (coachStatus == CoachStatus.ContactMade || coachStatus == CoachStatus.ContactAttempted || coachStatus == CoachStatus.NoActionRequired || coachStatus == CoachStatus.ReceivedVoucher));
    
    const statusOptions = coachStatusList
        .filter(f => f.isActive || f.id == coachStatus) //take out inactive status unless they're in use
        .sort((a,b) => a.sortOrder - b.sortOrder)
        .map(s => <option key={s.id} value={s.id} disabled={!s.isActive}>{s.description}</option>);

    
    const save = () => {
        onCoachStatusChange(data, {
            newCoachStatus: coachStatus,
            isCoachingDesired: isCoachingDesired,
            nextFollowupDate: nextFollowUpDate,
            mostRecentContactDate: mostRecentContactDate,
            transplantDate: transplantDate
        });
    }
    
    const transplantedStatuses = [
        CoachStatus.TransplantedDeceased,
        CoachStatus.TransplantedLiving
    ];

    const transPlantDateValid = !transplantedStatuses.some(f => f == coachStatus) || (
        transplantDate.trim() != '' 
        && moment(transplantDate).isValid() 
        && moment(transplantDate).isSameOrBefore(moment(), 'day')
    );
    const nextFollowUpDateValid = !nextFollowUpDateIsRequired || (!nextFollowUpDateIsPast && nextFollowUpDate.trim() != '' && moment(nextFollowUpDate).isValid())

    const allValid = transPlantDateValid && nextFollowUpDateValid

    return (
        <ViewWrapper> 
            <Panel>
                <Panel.Body style={{ padding: '10px' }}>
                    <Row>
                        <Col sm={12} md={12} >
                            <FormGroup>
                                <ControlLabel>Coach Status</ControlLabel>
                                <FormControl
                                    componentClass='select'
                                    name='type'
                                    value={coachStatus}
                                    onChange={(e: any) => setCoachStatus(e.target.value)}>
                                    <option value={-1} disabled>- Select -</option>
                                    {statusOptions}
                                </FormControl>
                            </FormGroup>
                        </Col>
                    </Row>
                    {transplantedStatuses.some(f => f == coachStatus) && <Row>
                        <Col sm={12} md={12}>
                            <FormGroup validationState={!transPlantDateValid ? 'error' : null}>
                                <ControlLabel>Transplant date</ControlLabel>
                                <FormControl
                                    type='date'
                                    name='transplantDate'
                                    value={transplantDate}
                                    max={moment(new Date()).format('YYYY-MM-DD')}
                                    onChange={(e: any) => setTransplantDate(e.target.value)}
                                />                                   
                            </FormGroup>
                        </Col>
                    </Row>}
                    <Row>
                        <Col sm={12} md={12}>
                            <FormGroup  validationState={!nextFollowUpDateValid ? 'error' : null}>
                                <ControlLabel>Next Follow-Up</ControlLabel>
                                <FormControl
                                    type='date'
                                    name='nextFollowUpDate'
                                    value={nextFollowUpDate}
                                    onChange={(e: any) => setNextFollowUpDate(e.target.value)}
                                />                                   
                                {nextFollowUpDateIsPast && <Label bsStyle={'danger'}>The next follow-up date is in the past</Label>}
                                {nextFollowUpDateIsToday && <Label bsStyle={'warning'}>The next follow-up date is today</Label>}
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={12}>
                            <FormGroup>
                                <ControlLabel>Most Recent Contact Attempt</ControlLabel>
                                <FormControl
                                    type='date'
                                    name='mostRecentContactDate'
                                    value={mostRecentContactDate}
                                    onChange={(e: any) => setMostRecentContactDate(e.target.value)}
                                />  
                                <Checkbox style={{marginLeft: 5}} checked={isCoachingDesired} onClick={() => setIsCoachingDesired(!isCoachingDesired)}><strong>Patient opted into coaching</strong></Checkbox>                                 
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={12}>
                            <Button bsStyle="primary" onClick={save} disabled={!allValid} block>
                                <Glyphicon glyph='save' />{' '}Save Changes
                            </Button>
                        </Col>
                    </Row>
                </Panel.Body>
            </Panel>
        </ViewWrapper>
    );

}

